import type { OfferType, ProgramConfig, RoleCapacity } from '../types/volunteer';

export type RoleStatus = 'available' | 'low' | 'exhausted' | 'none';

export interface CapacitySummary {
  totalOpportunities: number;
  roleClaimed: number;
  unassignedClaims: number;
  activeReservations: number;
  available: number;
}

export function roleRemaining(role: RoleCapacity): number {
  return Math.max(0, role.opportunities - role.claimed);
}

export function roleStatus(role: RoleCapacity): RoleStatus {
  if (role.opportunities === 0) return 'none';
  const remaining = roleRemaining(role);
  if (remaining === 0) return 'exhausted';
  if (remaining === 1) return 'low';
  return 'available';
}

/**
 * Prototype assumption (annotated as unresolved in the UI):
 * program availability = total opportunities − all claims − all active reservations,
 * because a reservation cannot yet be assigned to a specific role.
 */
export function capacitySummary(
  config: ProgramConfig,
  unassignedClaims: number,
  activeReservations: number,
): CapacitySummary {
  const totalOpportunities = config.roles.reduce((sum, r) => sum + r.opportunities, 0);
  const roleClaimed = config.roles.reduce((sum, r) => sum + Math.min(r.claimed, r.opportunities), 0);
  const available = Math.max(0, totalOpportunities - roleClaimed - unassignedClaims - activeReservations);
  return { totalOpportunities, roleClaimed, unassignedClaims, activeReservations, available };
}

/** Precedence: available Volunteer Registration Discount first, otherwise Automated Coach Credit. */
export function resolveOffer(config: ProgramConfig, availableForNew: number, holdsReservation: boolean): OfferType {
  if (config.discountEnabled && config.amount > 0 && (holdsReservation || availableForNew > 0)) {
    return 'discount';
  }
  return config.coachCreditEnabled ? 'coachCredit' : 'none';
}
